/**
 * The backoffice's imports, as viewer-api's `/api/admin/import` answers them: import batches
 * (`/api/admin/import/batches`).
 *
 * Every import is a batch (gdata-backend `docs/plans/import-batches.md`): a data manager's list of
 * `CLIENT/PATIENT` lines (the backoffice's uploads were removed on 2026-10-16). A batch has lines, a line has
 * files, and a file is done once exported to BigQuery or found already on the platform.
 *
 * Every shape the backoffice reads or sends is here, in one file, so a change of the backend contract
 * is one edit.
 */

/** Where a batch stands, derived by viewer-api from its counters and its last activity. */
export type ImportBatchStatus =
  | 'PLANNING'
  | 'IMPORTING'
  | 'EXPORTING'
  | 'STALLED'
  | 'COMPLETED'
  | 'COMPLETED_WITH_ERRORS'
  | 'FAILED'
  | 'CANCELLED';

export const IMPORT_BATCH_STATUSES: ImportBatchStatus[] = [
  'PLANNING',
  'IMPORTING',
  'EXPORTING',
  'STALLED',
  'COMPLETED',
  'COMPLETED_WITH_ERRORS',
  'FAILED',
  'CANCELLED',
];

/** Nothing will change without someone acting: the backoffice stops refreshing. */
export function isFinalStatus(status: ImportBatchStatus): boolean {
  return status === 'COMPLETED' || status === 'COMPLETED_WITH_ERRORS' || status === 'FAILED' ||
    status === 'CANCELLED';
}

/** A list of `CLIENT/PATIENT` lines: the only kind since the backoffice's uploads were removed (2026-10-16). */
export type ImportBatchKind = 'LIST';

/** The orchestrator's own work on the batch: listing its lines and sending its files. */
export type ImportBatchState = 'PLANNING' | 'PLANNED' | 'FAILED' | 'CANCELLED';

/**
 * The counters over files, as the orchestrator keeps them. A file is in one status, and adds to:
 * `imported` when imported (exported and export errors included), `exported`, `importErrors`,
 * `exportErrors`. See `fileBreakdown` for the disjoint parts.
 */
export type ImportCounters = {
  files: number;
  imported: number;
  exported: number;
  importErrors: number;
  exportErrors: number;
};

/** A batch's lines by status. `notFound` lines (nothing under the prefix) are among `error`. */
export type ImportLineCounts = {
  total: number;
  toDo: number;
  onGoing: number;
  done: number;
  error: number;
  notFound: number;
};

export type ImportBatch = {
  id: number;
  requestId: string;
  kind: ImportBatchKind;
  /** The list's file name. */
  name: string;
  /** The bucket every line is read from. */
  bucket: string;
  lineCount: number;
  requestedBy: string;
  requestedAt: string;
  state: ImportBatchState;
  status: ImportBatchStatus;
  /** Why the planning failed, when it did. */
  error: string | null;
  counters: ImportCounters;
  lines: ImportLineCounts;
  startedAt: string | null;
  plannedAt: string | null;
  /** The latest move of the batch or of any of its files. */
  progressAt: string | null;
  version: number;
  /** The W3C trace id of the operation's work: 32 lowercase hex characters; null when unknown. */
  traceId?: string | null;
  /** A Grafana Explore URL of the operation's logs, built by viewer-api; null when unknown. */
  logsUrl?: string | null;
};

export type ImportLineStatus = 'TO_DO' | 'ON_GOING' | 'DONE' | 'ERROR';

export const IMPORT_LINE_STATUSES: ImportLineStatus[] = ['TO_DO', 'ON_GOING', 'DONE', 'ERROR'];

export type ImportBatchLine = {
  /** `CLIENT/PATIENT[/…]/`, or one file. */
  prefix: string;
  client: string;
  patientId: string;
  /** Where it appears in the list; more than one is a repeated line. */
  lineNumbers: number[];
  status: ImportLineStatus;
  /** Listed, and nothing under it. */
  notFound: boolean;
  counters: ImportCounters;
  bytes: number;
  /** The platform's patient id, once a file of it is imported. */
  patient: string | null;
  version: number;
  updatedAt: string | null;
};

export type ImportFileStatus =
  | 'TO_DO'
  | 'IMPORTED'
  | 'EXPORTED'
  | 'IMPORT_ERROR'
  | 'EXPORT_ERROR';

export const IMPORT_FILE_STATUSES: ImportFileStatus[] = [
  'TO_DO',
  'IMPORTED',
  'EXPORTED',
  'IMPORT_ERROR',
  'EXPORT_ERROR',
];

export type ImportBatchFile = {
  path: string;
  generation: number;
  /** The line it belongs to. */
  line: string;
  status: ImportFileStatus;
  /** Why it failed, for the two error statuses. */
  reason: string | null;
  /** The importer that imported it (the commit of its image). */
  importerVersion: string | null;
  size: number | null;
  updatedAt: string | null;
};

/** A Spring page, as viewer-api answers every list. */
export type Page<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  /** Zero-based. */
  number: number;
  size: number;
};

export type ImportBatchesQuery = {
  page?: number;
  /** 20 by default. */
  size?: number;
  status?: ImportBatchStatus;
};

export type ImportBatchLinesQuery = {
  page?: number;
  size?: number;
  statuses?: ImportLineStatus[];
};

export type ImportBatchFilesQuery = {
  page?: number;
  size?: number;
  /** One line's files only. */
  line?: string;
  statuses?: ImportFileStatus[];
};

/** What viewer-api accepted: the batch is created by the orchestrator, found later by `requestId`. */
export type ImportSubmitted = {
  requestId: string;
  name: string;
  bucket: string;
  lines: number;
  distinct: number;
  duplicates: number;
};

/** The buckets a list may be read from (viewer-api's `imports.batches.source-buckets`). */
export type ImportSources = {
  buckets: string[];
};

/** A data manager's list: one `CLIENT/PATIENT` per line, read from `bucket`. */
export type SubmitListRequest = {
  file: Blob;
  /** The file's name: the batch's name unless `name` is given. */
  fileName: string;
  bucket: string;
  name?: string;
};

/** A line of a refused list, as the list numbers it (from 1). */
export type InvalidListLine = {
  line: number;
  text: string;
};
